import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bike, Car, Pencil, Plus, ShieldAlert, Trash2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { KpiCard, Panel, DataTable, type DataColumn } from "@/components/admin/analytics/AnalyticsKit";
import {
  FieldGroup,
  NotesField,
  SelectField,
  TextField,
} from "@/components/admin/delivery/form-bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toneClass } from "@/lib/admin/order-badges";
import {
  deleteDeliveryVehicle,
  listDeliveryPartners,
  listDeliveryVehicles,
  saveDeliveryVehicle,
} from "@/lib/admin/delivery.functions";
import {
  FUEL_TYPES,
  OWNER_TYPES,
  VEHICLE_STATUSES,
  VEHICLE_STATUS_LABEL,
  VEHICLE_TYPES,
  VEHICLE_TYPE_LABEL,
  isExpired,
  statusTone,
  vehicleSchema,
  type DeliveryVehicle,
  type VehicleInput,
} from "@/lib/admin/delivery.schemas";

export const Route = createFileRoute("/_admin/admin/vehicles")({
  head: () => ({
    meta: [
      { title: "Vehicles — Admin" },
      {
        name: "description",
        content:
          "Delivery fleet master data: registration, ownership, compliance documents and maintenance schedule.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: VehiclesPage,
});

function emptyForm(): VehicleInput {
  return {
    vehicle_number: "",
    vehicle_name: "",
    vehicle_type: "bike",
    owner_name: "",
    owner_type: "company",
    fuel_type: "petrol",
    assigned_driver_id: null,
    insurance_expiry: null,
    rc_number: "",
    rc_expiry: null,
    puc_expiry: null,
    service_due_date: null,
    status: "available",
    notes: "",
  };
}

function fromRow(v: DeliveryVehicle): VehicleInput {
  return {
    vehicle_number: v.vehicle_number,
    vehicle_name: v.vehicle_name,
    vehicle_type: (v.vehicle_type as VehicleInput["vehicle_type"]) ?? "bike",
    owner_name: v.owner_name,
    owner_type: (v.owner_type as VehicleInput["owner_type"]) ?? "company",
    fuel_type: (v.fuel_type as VehicleInput["fuel_type"]) ?? "petrol",
    assigned_driver_id: v.assigned_driver_id,
    insurance_expiry: v.insurance_expiry,
    rc_number: v.rc_number,
    rc_expiry: v.rc_expiry,
    puc_expiry: v.puc_expiry,
    service_due_date: v.service_due_date,
    status: v.status,
    notes: v.notes,
  };
}

function expiryCell(date: string | null) {
  if (!date) return <span className="text-muted-foreground">—</span>;
  return isExpired(date) ? (
    <span className="font-medium text-destructive">{date} · expired</span>
  ) : (
    <span>{date}</span>
  );
}

function VehiclesPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listDeliveryVehicles);
  const partnersFn = useServerFn(listDeliveryPartners);
  const saveFn = useServerFn(saveDeliveryVehicle);
  const deleteFn = useServerFn(deleteDeliveryVehicle);

  const vehicles = useQuery({
    queryKey: ["admin", "delivery", "vehicles"],
    queryFn: () => listFn(),
    staleTime: 10_000,
  });
  const partners = useQuery({
    queryKey: ["admin", "delivery", "partners"],
    queryFn: () => partnersFn(),
    staleTime: 60_000,
  });

  const [editing, setEditing] = useState<DeliveryVehicle | "new" | null>(null);
  const [form, setForm] = useState<VehicleInput>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<DeliveryVehicle | null>(null);

  useEffect(() => {
    if (editing === "new") setForm(emptyForm());
    else if (editing) setForm(fromRow(editing));
    setErrors({});
  }, [editing]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin", "delivery"] });

  const save = useMutation({
    mutationFn: (v: { id: string | null; patch: VehicleInput }) => saveFn({ data: v as never }),
    onSuccess: async () => {
      toast.success("Vehicle saved");
      setEditing(null);
      await invalidate();
    },
    onError: (e: Error) =>
      toast.error(
        e.message === "duplicate"
          ? "That vehicle number already exists."
          : "Couldn't save vehicle.",
      ),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } as never }),
    onSuccess: async () => {
      toast.success("Vehicle removed");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: () => toast.error("Couldn't remove vehicle."),
  });

  const rows = useMemo(() => vehicles.data ?? [], [vehicles.data]);
  const kpi = useMemo(() => {
    const total = rows.length;
    const available = rows.filter((r) => r.status === "available").length;
    const inService = rows.filter((r) => r.status === "in_service").length;
    const maintenance = rows.filter((r) => r.status === "maintenance").length;
    const compliance = rows.filter(
      (r) => isExpired(r.insurance_expiry) || isExpired(r.puc_expiry) || isExpired(r.rc_expiry),
    ).length;
    const serviceDue = rows.filter((r) => isExpired(r.service_due_date)).length;
    return { total, available, inService, maintenance, compliance, serviceDue };
  }, [rows]);

  const driverOptions = [
    { value: "none", label: "Unassigned" },
    ...(partners.data ?? []).map((p) => ({
      value: p.id,
      label: `${p.full_name} (${p.employee_code})`,
    })),
  ];

  const columns: Array<DataColumn<DeliveryVehicle>> = [
    {
      key: "number",
      header: "Vehicle number",
      render: (r) => <span className="font-mono text-xs font-medium">{r.vehicle_number}</span>,
      sortValue: (r) => r.vehicle_number,
      exportValue: (r) => r.vehicle_number,
    },
    {
      key: "name",
      header: "Vehicle name",
      render: (r) => r.vehicle_name || "—",
      sortValue: (r) => r.vehicle_name,
      exportValue: (r) => r.vehicle_name,
    },
    {
      key: "type",
      header: "Type",
      render: (r) => VEHICLE_TYPE_LABEL[r.vehicle_type] ?? r.vehicle_type,
      sortValue: (r) => r.vehicle_type,
      exportValue: (r) => VEHICLE_TYPE_LABEL[r.vehicle_type] ?? r.vehicle_type,
    },
    {
      key: "driver",
      header: "Assigned driver",
      render: (r) => r.assigned_driver_name || "—",
      sortValue: (r) => r.assigned_driver_name,
      exportValue: (r) => r.assigned_driver_name,
    },
    {
      key: "owner",
      header: "Owner",
      render: (r) => `${r.owner_name || "—"} · ${r.owner_type}`,
      exportValue: (r) => `${r.owner_name} (${r.owner_type})`,
    },
    {
      key: "fuel",
      header: "Fuel",
      render: (r) => r.fuel_type,
      exportValue: (r) => r.fuel_type,
    },
    {
      key: "insurance",
      header: "Insurance expiry",
      render: (r) => expiryCell(r.insurance_expiry),
      sortValue: (r) => r.insurance_expiry ?? "",
      exportValue: (r) => r.insurance_expiry ?? "",
    },
    {
      key: "rc",
      header: "RC expiry",
      render: (r) => expiryCell(r.rc_expiry),
      exportValue: (r) => r.rc_expiry ?? "",
    },
    {
      key: "puc",
      header: "PUC expiry",
      render: (r) => expiryCell(r.puc_expiry),
      exportValue: (r) => r.puc_expiry ?? "",
    },
    {
      key: "service",
      header: "Service due",
      render: (r) => expiryCell(r.service_due_date),
      sortValue: (r) => r.service_due_date ?? "",
      exportValue: (r) => r.service_due_date ?? "",
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge className={toneClass(statusTone(r.status))}>{VEHICLE_STATUS_LABEL[r.status]}</Badge>
      ),
      sortValue: (r) => r.status,
      exportValue: (r) => VEHICLE_STATUS_LABEL[r.status],
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="outline" onClick={() => setEditing(r)}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
          </Button>
        </div>
      ),
      exportValue: () => "",
    },
  ];

  const set = <K extends keyof VehicleInput>(key: K, value: VehicleInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = () => {
    const parsed = vehicleSchema.safeParse(form);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] = issue.message;
      setErrors(next);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    save.mutate({
      id: editing === "new" || editing === null ? null : editing.id,
      patch: parsed.data,
    });
  };

  return (
    <div>
      <AdminPageHeader
        title="Vehicles"
        description="Fleet master data. Dispatch only offers vehicles that are active and compliant."
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <KpiCard label="Total vehicles" value={String(kpi.total)} tone="info" icon={Car} loading={vehicles.isLoading} />
        <KpiCard label="Available" value={String(kpi.available)} tone="success" icon={Bike} loading={vehicles.isLoading} />
        <KpiCard label="In service" value={String(kpi.inService)} tone="teal" icon={Bike} loading={vehicles.isLoading} />
        <KpiCard label="Under maintenance" value={String(kpi.maintenance)} tone="warn" icon={Wrench} loading={vehicles.isLoading} />
        <KpiCard label="Document expired" value={String(kpi.compliance)} tone="danger" icon={ShieldAlert} loading={vehicles.isLoading} />
        <KpiCard label="Service overdue" value={String(kpi.serviceDue)} tone="purple" icon={Wrench} loading={vehicles.isLoading} />
      </div>

      <Panel title="Fleet register" subtitle={`${rows.length} vehicles`}>
        <div className="mb-3 flex justify-end">
          <Button size="sm" className="gap-1.5" onClick={() => setEditing("new")}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add vehicle
          </Button>
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          loading={vehicles.isLoading}
          searchOn={(r) =>
            `${r.vehicle_number} ${r.vehicle_name} ${r.owner_name} ${r.assigned_driver_name} ${r.vehicle_type}`
          }
          pageSize={10}
          exportName="delivery-vehicles"
          importModule="vehicles"
          minWidth={1000}
        />
      </Panel>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New vehicle" : "Edit vehicle"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-3">
            <FieldGroup title="Vehicle details">
              <TextField
                label="Vehicle number"
                placeholder="MH 01 AB 1234"
                value={form.vehicle_number}
                onChange={(v) => set("vehicle_number", v.toUpperCase())}
                error={errors["vehicle_number"]}
              />
              <TextField
                label="Vehicle name"
                value={form.vehicle_name}
                onChange={(v) => set("vehicle_name", v)}
              />
              <SelectField
                label="Vehicle type"
                value={form.vehicle_type}
                onChange={(v) => set("vehicle_type", v as VehicleInput["vehicle_type"])}
                options={VEHICLE_TYPES.map((t) => ({
                  value: t,
                  label: VEHICLE_TYPE_LABEL[t] ?? t,
                }))}
              />
              <SelectField
                label="Fuel type"
                value={form.fuel_type}
                onChange={(v) => set("fuel_type", v as VehicleInput["fuel_type"])}
                options={FUEL_TYPES.map((t) => ({ value: t, label: t }))}
              />
              <TextField
                label="Owner name"
                value={form.owner_name}
                onChange={(v) => set("owner_name", v)}
              />
              <SelectField
                label="Ownership"
                value={form.owner_type}
                onChange={(v) => set("owner_type", v as VehicleInput["owner_type"])}
                options={OWNER_TYPES.map((t) => ({ value: t, label: t }))}
              />
              <SelectField
                label="Assigned driver"
                value={form.assigned_driver_id ?? "none"}
                onChange={(v) => set("assigned_driver_id", v === "none" ? null : v)}
                options={driverOptions}
              />
              <SelectField
                label="Status"
                value={form.status}
                onChange={(v) => set("status", v as VehicleInput["status"])}
                options={VEHICLE_STATUSES.map((st) => ({
                  value: st,
                  label: VEHICLE_STATUS_LABEL[st],
                }))}
              />
            </FieldGroup>

            <FieldGroup
              title="Compliance & maintenance"
              hint="Expired insurance blocks the vehicle from being dispatched."
            >
              <TextField
                label="RC number"
                value={form.rc_number}
                onChange={(v) => set("rc_number", v)}
              />
              <TextField
                label="RC expiry"
                type="date"
                value={form.rc_expiry ?? ""}
                onChange={(v) => set("rc_expiry", v || null)}
              />
              <TextField
                label="Insurance expiry"
                type="date"
                value={form.insurance_expiry ?? ""}
                onChange={(v) => set("insurance_expiry", v || null)}
              />
              <TextField
                label="PUC expiry"
                type="date"
                value={form.puc_expiry ?? ""}
                onChange={(v) => set("puc_expiry", v || null)}
              />
              <TextField
                label="Next service due"
                type="date"
                value={form.service_due_date ?? ""}
                onChange={(v) => set("service_due_date", v || null)}
              />
              <NotesField value={form.notes} onChange={(v) => set("notes", v)} />
            </FieldGroup>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save vehicle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove vehicle?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.vehicle_number} will no longer be selectable in Dispatch or Shift
              Planning.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
